import { useMutation } from "@apollo/client"
import {
    CreatePaymentMethod,
    CreatePaymentMethodForm,
} from "@pallet/features/CustomerType/CreatePaymentMethod"
import { Button, Divider, Modal, notify, Text } from "@pallet-hq/hegel"
import { useFormikContext } from "formik"
import mixpanel from "mixpanel-browser"
import { useRouter } from "next/router"
import pluralize from "pluralize"
import { useState } from "react"
import { TRACK_EVENTS } from "constants/events"
import { useChangePartnershipTierModal } from "./hooks"
import { UPDATE_PARTNERSHIP_TIER } from "./mutations"
import { PartnershipChangePartnershipTier } from "__generated__/PartnershipChangePartnershipTier"

const ChangePartnershipTierModalComponent = () => {
    const router = useRouter()
    const {
        isModalOpen,
        hideModal: hideConfirmModal,
        newItem,
        partnership,
        changeType,
        recruitingTeam,
        shouldCollectPayment,
    } = useChangePartnershipTierModal()
    const [updateTier] = useMutation<PartnershipChangePartnershipTier>(
        UPDATE_PARTNERSHIP_TIER
    )
    const { submitForm } = useFormikContext()
    const [submitting, setSubmitting] = useState<boolean>(false)

    const handleClick = async () => {
        setSubmitting(true)

        let paymentMethod: string | null | undefined = undefined
        if (shouldCollectPayment) {
            paymentMethod = (await submitForm()) as unknown as string | null
        }
        if (paymentMethod || !shouldCollectPayment) {
            const resp = await updateTier({
                variables: {
                    partnership: partnership?.uuid,
                    item: newItem?.uuid,
                    paymentMethod,
                },
            })
            if (resp.data?.partnershipChangePartnershipTier?.partnership?.id) {
                notify.success(
                    `${
                        changeType === "upgrade"
                            ? "Upgraded"
                            : changeType === "downgrade"
                            ? "Downgraded"
                            : "Changed"
                    } to ${newItem?.collectivePass?.name}`,
                    {
                        autoClose: 7000,
                        action: "Manage job posts",
                        onAction: () =>
                            router.push(`/team/${recruitingTeam}/jobs`),
                    }
                )
                hideConfirmModal()
                mixpanel.track(TRACK_EVENTS.CHANGE_PLAN_COMPLETED, {
                    applylist_slug: partnership?.pallet.slug,
                    subscription_length: newItem?.recurringEnum,
                    action: changeType,
                })
            } else {
                notify.fail("Failed to update plan")
            }
        } else {
            notify.fail("Cannot update plan without a payment method")
        }
        setSubmitting(false)
    }

    const action =
        changeType === "upgrade"
            ? "Upgrade plan"
            : changeType === "downgrade"
            ? "Downgrade plan"
            : "Change plan"

    const oldNumPosts = partnership?.numPosts ?? 0
    const oldPostString = pluralize(
        `${partnership?.isFeatured ? "featured" : "standard"} job post`,
        oldNumPosts,
        true
    )
    const newNumPosts = newItem?.collectivePass?.activeJobPostLimit ?? 0
    const newPostString = pluralize(
        `${
            newItem?.collectivePass?.isFeatured ? "featured" : "standard"
        } job post`,
        newNumPosts,
        true
    )

    return (
        <Modal
            isOpen={isModalOpen}
            hideModal={hideConfirmModal}
            title={action}
            width="small"
            actions={
                <>
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={hideConfirmModal}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        onClick={handleClick}
                        loading={submitting}
                    >
                        {action}
                    </Button>
                </>
            }
        >
            {`You are about to subscribe to ${
                newItem?.collectivePass?.name
            } for ${newItem?.formattedPrice} per ${
                newItem?.recurringEnum ?? ""
            }, and ${
                newNumPosts < oldNumPosts
                    ? "decrease"
                    : newNumPosts > oldNumPosts
                    ? "increase"
                    : "change"
            } your available active job posts from ${oldPostString} to ${newPostString}.`}
            <Text variant="paragraphSmall" color="gray600" mt="small">
                {changeType === "upgrade"
                    ? "Changes to your partnership plan will take effect immediately, and you will be charged a prorated amount."
                    : changeType === "change plan"
                    ? "Changes to your partnership plan will take effect immediately."
                    : "Changes to your partnership plan will take effect immediately. The difference in tier prices will be credited to your billing account and applied to future payments."}
            </Text>
            {shouldCollectPayment && (
                <>
                    <Divider my="small" />
                    <CreatePaymentMethodForm />
                </>
            )}
        </Modal>
    )
}

export const ChangePartnershipTierModal = () => (
    <CreatePaymentMethod>
        <ChangePartnershipTierModalComponent />
    </CreatePaymentMethod>
)
